/**
 * Header "Sync Data" button (live/analytics DB split).
 *
 * The analytics DB (simulations, market data, analytics pages, Grafana)
 * is a copy of the trading-bot's live DB, refreshed on demand rather than
 * on a timer. This button is both the manual trigger and the freshness
 * indicator ("Synced 3m ago"). When the "Auto-sync analytics data on
 * navigation" setting is ON (default), it also AUTO-triggers a sync when the
 * operator navigates to a data-consuming route (market data / analytics /
 * simulators / grafana), throttled client-side; the server single-flights
 * and coalesces overlapping requests, so spamming it is harmless. When the
 * setting is OFF, only manual clicks (and server-side correctness syncs)
 * refresh the analytics DB.
 *
 * Hidden entirely in single-DB mode (`mode === "single"`), where analytics
 * reads go straight to the live DB and there is nothing to sync.
 */
import { useEffect, useMemo } from "react";
import { ActionIcon, Group, Loader, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";

import { api } from "../api/client";
import { QK } from "../queryClient";
import { useAutoSyncOnNav } from "../lib/autoSyncPref";

/** Routes whose data comes from the analytics DB -> auto-sync on entry. */
const AUTO_SYNC_ROUTE_PREFIXES = ["/market-data", "/analytics", "/grafana"];

/** Client-side floor between auto-triggered syncs (manual clicks bypass). */
const AUTO_SYNC_MIN_INTERVAL_MS = 15_000;

// Module-level so route remounts share the throttle.
let lastAutoSyncAt = 0;

function isAutoSyncRoute(pathname: string): boolean {
  return AUTO_SYNC_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatBytes(n: number | null): string {
  if (n == null) return "…";
  if (n >= 1 << 30) return `${(n / (1 << 30)).toFixed(1)} GB`;
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(0)} MB`;
  return `${Math.max(1, Math.floor(n / 1024))} KB`;
}

/** `compact` (phone header) renders just the icon; the status text moves
 *  into the tooltip. */
export function SyncButton({ compact = false }: { compact?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { location } = useRouterState();
  const [autoSyncOnNav] = useAutoSyncOnNav();

  const statusQuery = useQuery({
    queryKey: QK.syncStatus,
    queryFn: () => api.sync.status(),
    refetchInterval: (query) => {
      const st = query.state.data?.state;
      return st === "syncing" || st === "initial_build" ? 2_000 : 30_000;
    },
    staleTime: 5_000,
    retry: false,
  });
  const status = statusQuery.data;

  const syncMutation = useMutation({
    mutationFn: () => api.sync.run(),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: QK.syncStatus });
    },
    onSuccess: async () => {
      // Fresh analytics data just landed — refresh everything on screen.
      await queryClient.invalidateQueries();
    },
  });

  const syncing = syncMutation.isPending || status?.state === "syncing";
  const building = status?.state === "initial_build";
  const errored = status?.state === "error" || syncMutation.isError;

  // Auto-trigger on entering a data-consuming route (throttled; the
  // server additionally single-flights + coalesces).
  const autoRoute = isAutoSyncRoute(location.pathname);
  const canSync = status?.mode === "split" && !building;
  useEffect(() => {
    if (!autoSyncOnNav || !autoRoute || !canSync || syncMutation.isPending) return;
    const now = Date.now();
    if (now - lastAutoSyncAt < AUTO_SYNC_MIN_INTERVAL_MS) return;
    lastAutoSyncAt = now;
    syncMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSyncOnNav, autoRoute, canSync, location.pathname]);

  const subtitle = useMemo(() => {
    if (building) return `building ${formatBytes(status?.initial_build_bytes ?? null)}`;
    if (syncing) return "syncing…";
    if (errored) return "sync error";
    return `Synced ${relativeTime(status?.last_synced_at ?? null)}`;
  }, [building, syncing, errored, status?.initial_build_bytes, status?.last_synced_at]);

  // Single-DB mode (or endpoint unavailable): nothing to sync — hide.
  if (!status || status.mode !== "split") return null;

  const tooltip = errored
    ? (status.last_error ?? "sync failed — click to retry")
    : building
      ? "One-time analytics DB build in progress"
      : `Copy the latest live trading data into the analytics DB (used by market data, analytics, simulations and Grafana).${
        status.last_rows_copied != null ? ` Last sync: ${status.last_rows_copied} row(s).` : ""
      }`;

  const icon = syncing || building ? (
    <Loader size={18} />
  ) : errored ? (
    <IconAlertTriangle size={18} color="var(--mantine-color-red-6)" />
  ) : (
    <IconRefresh size={18} />
  );
  const onClick = () => {
    if (!syncing && !building) {
      lastAutoSyncAt = Date.now();
      syncMutation.mutate();
    }
  };

  if (compact) {
    return (
      <Tooltip label={`${subtitle} — ${tooltip}`} multiline maw={320}>
        <ActionIcon
          variant="default"
          size={36}
          radius="md"
          aria-label={`Sync analytics data (${subtitle})`}
          onClick={onClick}
          style={{ opacity: building ? 0.7 : 1, cursor: building ? "wait" : "pointer" }}
        >
          {icon}
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={tooltip} multiline maw={320}>
      <UnstyledButton
        aria-label="Sync analytics data"
        onClick={onClick}
        style={{
          borderRadius: 10,
          padding: "6px 12px",
          border: "1px solid var(--mantine-color-default-border)",
          background: "var(--mantine-color-default)",
          opacity: building ? 0.7 : 1,
          cursor: building ? "wait" : "pointer",
        }}
      >
        <Group gap={10} wrap="nowrap" align="center">
          {icon}
          <div>
            <Text size="xs" c="dimmed" style={{ lineHeight: 1.1 }}>
              Sync Data
            </Text>
            <Text
              size="sm"
              fw={700}
              c={errored ? "red" : undefined}
              style={{ lineHeight: 1.1 }}
            >
              {subtitle}
            </Text>
          </div>
        </Group>
      </UnstyledButton>
    </Tooltip>
  );
}
